// Componente indicatore stato offline per PWA
import React from 'react';
import styled from 'styled-components';
import { Wifi, WifiOff, Download, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { useOffline } from '../hooks/useOffline';
import { ricetteOfflineService } from '../services/ricetteOffline';

const OfflineIndicator = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 70px;
  right: 20px;
  z-index: 1000;
  background: ${props => props.isVisible ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  border: 1px solid #ddd;
  border-radius: 12px;
  padding: 0.8rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  font-size: 0.9rem;
  opacity: ${props => props.isVisible ? 1 : 0};
  pointer-events: ${props => props.isVisible ? 'all' : 'none'};

  @media (max-width: 768px) {
    top: 60px;
    right: 10px;
    left: 10px;
    font-size: 0.8rem;
    padding: 0.6rem 0.8rem;
  }
`;

const StatusBadge = styled.div<{ variant: 'online' | 'offline' | 'sync' | 'ready' | 'error' }>`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border-radius: 20px;
  font-weight: 500;
  font-size: 0.8rem;
  
  ${props => {
    switch (props.variant) {
      case 'online':
        return `
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;
      case 'offline':
        return `
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
      case 'sync':
        return `
          background: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        `;
      case 'ready':
        return `
          background: #cce5ff;
          color: #004085;
          border: 1px solid #99d6ff;
        `;
      case 'error':
        return `
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
    }
  }}
`;

const SyncButton = styled.button`
  background: #6c5ce7;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  transition: background 0.2s;

  &:hover {
    background: #5a4bd6;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const CacheInfo = styled.div`
  font-size: 0.7rem;
  color: #666;
  margin-top: 0.3rem;
`;

interface OfflineStatusProps {
  className?: string;
}

export const OfflineStatus: React.FC<OfflineStatusProps> = ({ className }) => {
  const { isOnline, serviceWorkerReady, cacheStatus, preloadRicetteData } = useOffline();
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [showDetails, setShowDetails] = React.useState(false);
  const [cacheInfo, setCacheInfo] = React.useState<any>(null);

  React.useEffect(() => {
    // Aggiorna info cache periodicamente
    const updateCacheInfo = () => {
      setCacheInfo(ricetteOfflineService.getCacheInfo());
    };

    updateCacheInfo();
    const interval = setInterval(updateCacheInfo, 5000);
    return () => clearInterval(interval);
  }, [cacheStatus]);

  const handleSync = async () => {
    if (!isOnline || isSyncing) return;

    setIsSyncing(true);
    try {
      // Sincronizza via service worker
      await preloadRicetteData();
      
      // Sincronizza anche localStorage
      await ricetteOfflineService.syncData();
      
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const getStatusBadge = () => {
    if (isSyncing) {
      return (
        <StatusBadge variant="sync">
          <Loader size={14} className="animate-spin" />
          Sincronizzazione...
        </StatusBadge>
      );
    }

    if (!isOnline) {
      return (
        <StatusBadge variant="offline">
          <WifiOff size={14} />
          Offline
        </StatusBadge>
      );
    }

    if (cacheStatus === 'ready') {
      return (
        <StatusBadge variant="ready">
          <CheckCircle size={14} />
          Ricette disponibili offline
        </StatusBadge>
      );
    }

    if (cacheStatus === 'error') {
      return (
        <StatusBadge variant="error">
          <AlertCircle size={14} />
          Errore cache
        </StatusBadge>
      );
    }

    return (
      <StatusBadge variant="online">
        <Wifi size={14} />
        Online
      </StatusBadge>
    );
  };

  // Mostra l'indicatore solo se offline o se ci sono info rilevanti
  const shouldShow = !isOnline || cacheStatus === 'ready' || isSyncing || showDetails;

  return (
    <OfflineIndicator 
      isVisible={shouldShow} 
      className={className}
      onClick={() => setShowDetails(!showDetails)}
    >
      <div>
        {getStatusBadge()}
        
        {isOnline && cacheStatus !== 'ready' && !isSyncing && (
          <SyncButton onClick={handleSync} disabled={!serviceWorkerReady}>
            <Download size={12} />
            Scarica ricette offline
          </SyncButton>
        )}

        {showDetails && cacheInfo && (
          <CacheInfo>
            {cacheInfo.available ? (
              <>
                📦 {cacheInfo.itemCount} elementi in cache
                <br />
                🕒 Ultimo aggiornamento: {cacheInfo.lastUpdate?.toLocaleString()}
              </>
            ) : (
              '📦 Cache vuota - scarica i dati per uso offline'
            )}
          </CacheInfo>
        )}
      </div>
    </OfflineIndicator>
  );
};

export default OfflineStatus;
